import { plural, Trans } from "@lingui/macro";
import ContactActions from 'bukku/redux/Contact/actions';
import FileActions from 'bukku/redux/File/actions';
import TransactionActions from 'bukku/redux/Transaction/actions';
import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLightboxHandler } from '../../FileUploaderListManager/components/FileUploaderAndListV2/components/FilesList/useLightboxHandler';
import { useFilesListConfig } from '../../FileUploaderListManager/components/FileUploaderAndListV2/hooks/useFilesListConfig';
import { FileListModule } from '../../FileUploaderListManager/enum/FileListModule';
import { useEffectSuccess } from '../../Providers/helpers/useCompare';

const {
    transactionRead,
} = TransactionActions;

const {
    readContact,
} = ContactActions;

const {
    readFile,
} = FileActions;

/**
 * Pops up Lightbox when the icon is clicked, will not be clickable if there are no files.
 * Props:
 * @param {FileListModule} type - TRANSACTION, CONTACT, FILE_MANAGER
 * @param {{id: number, type: string|null}} record - record object, type eg: sale_invoice, purchase_order
 */
export const useFilesPreviewHandler = (moduleType, record) => {
    const {
        transactionReadState,
        transaction,
    } = useSelector(state => state.Transaction, shallowEqual);
    const {
        contactReadState,
        contact,
        forceHideFilePreview,
    } = useSelector(state => state.Contact, shallowEqual);
    const {
        readFileState,
        file: fileManagerFile,
    } = useSelector(state => state.File, shallowEqual);

    const dispatch = useDispatch();

    // Fetch the clicked record - which contains files info
    const fetchClickedRecord = record => {
        switch (moduleType) {
            case FileListModule.TRANSACTION:
                dispatch(transactionRead(record.type, record.id));
                break;
            case FileListModule.CONTACT:
                dispatch(readContact(record.id));
                break;
            case FileListModule.FILE_MANAGER:
                dispatch(readFile(record.id));
                break;
            default:
                throw new Error(`Unkown type: ${moduleType}.`);
        }
    };

    const { files } = useFilesListConfig(moduleType);
    const { onFileItemClick } = useLightboxHandler(files, moduleType);

    // When the response is received, check if the response is for the clicked record.
    // This prevents invoking onFileItemClick() for multiple rows when the response is for another record.
    const requestId = record.id;
    useEffectSuccess(transactionReadState, () => {
        const responseId = transaction.id;
        if (requestId === responseId)
            onFileItemClick();
    });

    useEffectSuccess(contactReadState, () => {
        const responseId = contact.id;
        if (requestId === responseId && !forceHideFilePreview)
            onFileItemClick();
    });

    useEffectSuccess(readFileState, () => {
        const responseId = fileManagerFile.id;
        if (requestId === responseId)
            onFileItemClick();
    });

    const popoverContent =
        moduleType === FileListModule.FILE_MANAGER ?
            <Trans>Preview</Trans> :
            plural(record.file_count || 0, { one: '1 file', other: '# files'});

    const onClickRead = () => fetchClickedRecord(record);

    return {
        onClickRead,
        popoverContent,
    };
};
